import React, { useState, useEffect, useMemo, useRef } from 'react'
import qs from 'qs'
import styled from 'styled-components'
import { algoliaIndex } from '@/lib/algolia'
import media from 'styled-media-query'
import { useStaticRandom } from '../../hooks/useStaticRandom'
import SectionHeader from '@/components/SectionHeader'
import SectionHeadingLarge from '@/components/SectionHeadingLarge'
import SectionSubHeading from '@/components/SectionSubHeading'
import CenterFeedRender from './CenterFeedRender'
import HowWeSortResultsTooltip from './HowWeSortResultsTooltip'
import { createDataLayerIfNotExists } from '../../utils/global/createDataLayerIfNotExists'
import { getTermHeader } from '../../domain/term/headerMappings/getTermHeader'
import { CustomHeaderType } from '../../domain/term/headerMappings/constants'
import { addComponentsToFeed } from '../../domain/feed/insertIntoFeed'
import { computeFeedLayout } from '../../domain/feed/useCases/computeFeedLayout'
import { useMidFeedAds } from '../../hooks/useMidfeedAds'
import getPageLatLng from '../../domain/useCases/getPageLatLng'
import { getScore } from '../../domain/useCases/score/getScore.ts'
import { DEFAULT_RESULTS_PER_PAGE } from '../../domain/search/constants'
import { getFacetFromPageType } from '../../domain/useCases/getFacetFromPageType'
import {
  isNonEmptyArray,
  isNonEmptyObject,
  isNonEmptyString
} from '../../utils/checks'
import {
  isFacetPageType,
  isLocationPageType
} from '../../domain/useCases/pageTypeCheck'
import { getFeaturedCenters } from '@/domain/center/useCases/getFeaturedCenters'
import WhyTrustRecovery from './WhyTrustRecovery'
import FAQSection from './FAQSection.tsx'
import LocationAndTaxonomyAccordionFAQs from '../organisms/AccordionFAQs/LocationAndTaxonomyAccordionFAQs.tsx'
import { getSortedTermsByPageType } from '../../domain/FAQs/getSortedTermsByPageType.js'
import shouldUseNewUIFunc from '@/domain/useCases/shouldUseNewUI'
import { sortFeaturedCentersByScore } from '@/domain/useCases/score/sortFeaturedCentersByScore'
import { createNearbyCenterConfig } from '../../domain/feed/useCases/createNearbyCentersConfig.ts'
import { dedupeAndScoreNearbyCenters } from '../../domain/feed/useCases/dedupeAndScoreNearbyCenters'
import { createPrimaryFocusMap } from '../../domain/term/useCases/createPrimaryFocusMap'
import { getCountriesList } from '@/graphql-fragments/getCountriesList'
import { applyReverseTargeting } from '../../domain/useCases/score/applyReverseTargeting'
import { isSponsor } from '../../domain/center.js'

const pageSize = 20
const MAX_FACET_PAGE_HIT_COUNT = 100
const FACET_NEARBY_HITS_PER_PAGE_ALGOLIA = 50
const NEARBY_HITS_PER_PAGE_ALGOLIA = 15

export default function CenterFeed({
  children,
  condition,
  hasHero,
  hitsSource = 'gatsby',
  pageType,
  searchState,
  staticHits,
  hits: propsHits,
  termData,
  title,
  shouldDefaultRegionalize = false,
  customTermHeader,
  locationType,
  currentRefinements,
  pageData,
  hitCount,
  pageContext,
  customFAQs,
  parentLocation,
  sortedLocations,
  reverseTargeting,
  department
}) {
  const isLocationOrFacetPageType =
    isLocationPageType(pageType) || isFacetPageType(pageType)

  const shouldUseNewUI = shouldUseNewUIFunc(pageType, pageData?.location)

  const [dataLayerPushed, setDataLayerPushed] = useState(false)

  const isFacetPage = isFacetPageType(pageType)
  const midFeedAdsRef = useRef(useMidFeedAds())

  const [queryID, setQueryID] = useState(null)
  const [neighborCenters, setNeighborCenters] = useState([])
  const [algoliaFeaturedCenters, setFeaturedCenters] = useState([])
  const [featuredCentersLoading, setFeaturedCentersLoading] = useState(true)
  const [isNeighborCentersLoaded, setIsNeighborCentersLoaded] = useState(false)

  const allCountries = getCountriesList()
  const primaryFocusMap = createPrimaryFocusMap()
  const primaryFocusTerm = useMemo(
    () => primaryFocusMap[termData.title],
    [termData]
  )

  useEffect(() => {
    if (queryID == null && propsHits.length > 0) {
      setQueryID(propsHits[0].__queryID)
    }
  }, [propsHits])

  const { hits, feedLayout } = useMemo(() => {
    return {
      hits: staticHits,
      feedLayout: computeFeedLayout({
        centerCount: staticHits.length,
        pageType
      })
    }
  }, [staticHits])

  const { isPageTypeFacet, facet } = useMemo(() => {
    const facetFromPageType = getFacetFromPageType(pageType)
    return {
      isPageTypeFacet: !!facetFromPageType,
      facet: facetFromPageType
    }
  }, [pageType])

  //Organic
  const centersSortedByScore = useMemo(() => {
    const staticRandom = useStaticRandom()
    return hits
      .map((hit) => {
        const center = isSponsor(hit)
          ? applyReverseTargeting(hit, {
              term: pageData.term,
              location: pageData.location,
              reverseTargeting
            })
          : hit
        const score = getScore({
          center,
          searchState,
          staticRandom: staticRandom(),
          locationType,
          pageType,
          primaryFocus: primaryFocusTerm
        })
        return {
          ...center,
          highlighted_specializations: hit.new_specializations?.filter((term) =>
            Object.values(searchState).includes(term)
          ),
          score
        }
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, MAX_FACET_PAGE_HIT_COUNT)
  }, [hits, searchState])

  const adCenters = useMemo(() => {
    const midFeedUnsorted = midFeedAdsRef.current.filter(Boolean)

    const scoredAds = midFeedUnsorted.map((ad) => {
      const staticRandom = useStaticRandom()
      if (!ad) return undefined

      const score = getScore({
        center: ad,
        searchState,
        staticRandom: staticRandom(),
        locationType,
        primaryFocus: primaryFocusTerm,
        pageType
      })

      return {
        ...ad,
        highlighted_specializations: ad.new_specializations?.filter((term) =>
          Object.values(searchState).includes(term)
        ),
        score
      }
    })

    if (isLocationPageType(pageType)) {
      return scoredAds
    }
    return scoredAds.sort((a, b) => b.score - a.score)
  }, [centersSortedByScore, midFeedAdsRef])

  const { lat, lng } = useMemo(
    () =>
      getPageLatLng({
        location: pageData?.location,
        centerList: hits
      }),
    [pageData?.location, hits]
  )

  const getAlgoliaResultsCount = () =>
    isPageTypeFacet
      ? FACET_NEARBY_HITS_PER_PAGE_ALGOLIA
      : NEARBY_HITS_PER_PAGE_ALGOLIA

  const centerFeedHeader = getTermHeader({
    pageType,
    title,
    termTitle: termData.title,
    customTermHeader,
    shouldDefaultRegionalize,
    headerType: CustomHeaderType.CENTER_FEED,
    centerCount: hitCount || staticHits.length
  })

  const shouldLoadNeighborCenters = hits.length < pageSize

  const generateNeighborCenters = async () => {
    if (neighborCenters.length > 0 || !isLocationOrFacetPageType) return

    const config = createNearbyCenterConfig({
      lat,
      lng,
      title,
      parentLocation,
      locationType,
      hitsPerPage: getAlgoliaResultsCount(),
      isPageTypeFacet,
      facet,
      condition
    })
    if (!isNonEmptyObject(config)) {
      return
    }

    const centersBasedOnGeolocation = await algoliaIndex
      .search('', config)
      .then(({ hits }) => {
        return hits.filter(
          (hit) => hit._geoloc.lat !== lat && hit._geoloc.lng !== lng
        )
      })
      .catch((error) => {
        console.error('error setting nearby centers', error)
        return
      })
    const deduped = dedupeAndScoreNearbyCenters({
      consolidatedFeed,
      adCenters,
      nearbyCenters: centersBasedOnGeolocation,
      primaryFocusTerm,
      searchState,
      locationType,
      pageType,
      resultsToDisplay: getAlgoliaResultsCount()
    })
    setIsNeighborCentersLoaded(true)
    setNeighborCenters(deduped)
    return
  }

  useEffect(() => {
    if (shouldLoadNeighborCenters) {
      generateNeighborCenters()
    }
  }, [hits, pageType, termData])

  useEffect(() => {
    if (isLocationOrFacetPageType) {
      generateFeaturedCenters().then((centers) => {
        setFeaturedCenters(centers)
        setFeaturedCentersLoading(false)
      })
    }
  }, [pageData?.location?._geoloc])

  useEffect(() => {
    if (dataLayerPushed) {
      return
    }

    if (centersSortedByScore.length === 0) {
      return
    }

    // the below code pertains to Google Tag Manager and may have no context outside of this file; DO NOT DELETE.
    const browseItemList = centersSortedByScore
      .slice(0, 19)
      .map((center, i) => ({
        item_name: center.title,
        item_id: center.slug,
        item_list_name: 'Feed',
        index: i + 1,
        item_category: window.location.href
      }))

    createDataLayerIfNotExists()
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'view_item_list',
      ecommerce: {
        items: browseItemList
      }
    })
    setDataLayerPushed(true)
  }, [centersSortedByScore])

  const searchStateValues = useMemo(
    () => Object.values(searchState),
    [searchState]
  )

  const handleAssignPrimaryFocusToLocation =
    !isNonEmptyString(primaryFocusTerm) && isLocationPageType(pageType)
      ? 'Substance Use'
      : primaryFocusTerm

  const shouldDirectToBrowse = true

  const browseCTA = useMemo(() => {
    if (isLocationPageType(pageType)) {
      return `/browse/?${qs.stringify(
        searchState
      )}&referral=SeeAllCentersNotice`
    }
    return `/browse/?${qs.stringify(searchState)}`
  }, [pageType, searchState])

  const generateFeaturedCenters = async () => {
    if (!isLocationOrFacetPageType || !pageData?.location?._geoloc) {
      return []
    }

    const featuredCenters = await getFeaturedCenters({
      locationData: pageData.location,
      allCountries,
      hitsPerPage: DEFAULT_RESULTS_PER_PAGE,
      primaryFocus: primaryFocusTerm,
      termData
    })

    if (isNonEmptyArray(featuredCenters)) {
      return sortFeaturedCentersByScore({
        featuredCenters,
        searchState,
        locationType,
        primaryFocus: primaryFocusTerm,
        pageType
      })
    }
  }

  const renderHeaderTitle = ({ count, title, pageType }) => {
    if (count > 0) {
      const resultText = count === 1 ? 'Result' : 'Results'
      return (
        <>
          <span className="font-semibold">{count}</span> {resultText}
        </>
      )
    } else {
      return `Nearby results`
    }
  }

  const consolidatedFeed = useMemo(() => {
    return addComponentsToFeed({
      feedLayout: feedLayout,
      localFeed: [],
      organicFeed: centersSortedByScore,
      nearbyFeed: neighborCenters,
      midFeedAds: adCenters,
      scoringData: {
        locationType,
        searchState,
        title,
        pageType,
        primaryFocus: primaryFocusTerm
      }
    })
  }, [neighborCenters])

  return (
    <div className="relative">
      {/* Invisible div used to adjust scroll position due to the height of a sticky navbar */}
      <div
        id="browse-all"
        className="invisible absolute top-[-73px] h-0"
      ></div>
      <div>
        <Style>
          {shouldUseNewUI ? (
            <div className="flex items-center justify-between">
              <h2 className="text-sm text-gray-900">
                {renderHeaderTitle({
                  count: hitCount || staticHits.length,
                  title,
                  pageType
                })}
              </h2>
              <HowWeSortResultsTooltip />
            </div>
          ) : (
            <SectionHeader>
              {(hitsSource !== 'algolia' || hits.length > 0) && (
                <>
                  {hasHero && centerFeedHeader && hits.length > 0 ? (
                    <SectionHeadingLarge
                      id="browse-feed-title-with-length"
                      tag="h2"
                    >
                      {centerFeedHeader}
                    </SectionHeadingLarge>
                  ) : !hasHero && hits.length > 0 ? (
                    <SectionHeadingLarge
                      id="browse-feed-title-with-length"
                      tag="h2"
                    >
                      {centerFeedHeader}
                    </SectionHeadingLarge>
                  ) : hasHero && hits.length === 0 ? (
                    <SectionHeadingLarge
                      id="browse-feed-title-with-length-no-hits"
                      tag="h2"
                    >
                      Browse All Rehabs Near {title}
                    </SectionHeadingLarge>
                  ) : (
                    <SectionHeadingLarge
                      id="browse-feed-title-with-length-no-hits"
                      tag="h2"
                    >
                      Browse All Rehabs Near {title}
                    </SectionHeadingLarge>
                  )}
                </>
              )}
              <SectionSubHeading tag="div">
                <HowWeSortResultsTooltip />
              </SectionSubHeading>
            </SectionHeader>
          )}
          {/* children has the FilterContainer, see BrowseView.jsx */}
          {children}

          <div className="mx-auto max-w-screen-xl flex-col items-start gap-6 lg:flex lg:flex-row lg:gap-10">
            <div
              className={`${
                shouldUseNewUI ? 'lg:w-[calc(100%-440px)]' : 'lg:w-full'
              }`}
            >
              <CenterFeedRender
                feed={consolidatedFeed}
                activeFacets={searchStateValues}
                queryID={queryID}
                locationType={locationType}
                currentRefinements={currentRefinements}
                searchState={searchState}
                pageData={pageData}
                termData={termData}
                pageType={pageType}
                customTermHeader={customTermHeader}
                nationalFeaturedCenters={algoliaFeaturedCenters}
                pageContext={pageContext}
                title={title}
                pageTitle={termData.title}
                featuredCentersLoading={featuredCentersLoading}
                shouldDirectToBrowse={shouldDirectToBrowse}
                browseCTA={browseCTA}
                nearbyCentersLoading={!isNeighborCentersLoaded}
                hasNearbyCenters={neighborCenters.length > 0}
                termFocus={handleAssignPrimaryFocusToLocation}
                sortedLocations={sortedLocations}
                department={department}
              />
            </div>
            {shouldUseNewUI && (
              <div className="mt-8 lg:w-[400px] lg:shrink-0">
                <div className="flex flex-col gap-6">
                  <div className="hidden lg:block">
                    <WhyTrustRecovery />
                  </div>
                  <div className="border-t border-gray-300 pt-6 lg:border-none lg:pt-0">
                    <FAQSection>
                      <LocationAndTaxonomyAccordionFAQs
                        {...pageData.location}
                        {...pageData.term}
                        pageData={pageData.location || pageData.term}
                        pageType={pageType}
                        termData={termData}
                        customFAQs={customFAQs}
                        customTermHeader={pageData.term?.customHeaderText}
                        staticHits={
                          pageData[`centers_${pageType}`] &&
                          pageData[`centers_${pageType}`].edges.map(
                            ({ node }) => node
                          )
                        }
                        centerCount={hitCount}
                        terms={{
                          settings: getSortedTermsByPageType(
                            pageData,
                            pageType,
                            'settings'
                          ),
                          therapies: getSortedTermsByPageType(
                            pageData,
                            pageType,
                            'therapies'
                          ),
                          locations: getSortedTermsByPageType(
                            pageData,
                            pageType,
                            'locations'
                          ),
                          options: getSortedTermsByPageType(
                            pageData,
                            pageType,
                            'options'
                          ),
                          amenities: getSortedTermsByPageType(
                            pageData,
                            pageType,
                            'onsite_amenities',
                            'offsite_amenities'
                          )
                        }}
                        compact={true}
                      />
                    </FAQSection>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Style>
      </div>
    </div>
  )
}

const Style = styled.div`
  .load-more {
    max-width: fit-content;
  }
  .contain {
    ${media.greaterThan('large')`
      width: 70%;
      margin: 0 auto;
    `}
  }
`
